import React, { useEffect, useMemo, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import employeeService from '../../services/employeeService';
import addressService from '../../services/addressService';
import { formatDateBR, maskCPF, maskPhone } from '../../utils/masks';
import { mapAddressFromApi } from '../../utils/mapAddressFromApi';
import ReadOnlyAddressFields from '../shared/ReadOnlyAddressFields';
import TransportCompanyLogo, { ProfileLogoFrame, ProfileLogoWrap } from '../shared/TransportCompanyLogo';
import { useAuth } from '../../context/AuthContext';
import { TRANSPORT_COMPANY_LOGO_FORMATS } from '../../constants/transportCompanyLogo';

const { Container, Header, Button, SectionTitle, FormRow, FormGroup } = CrudStyles;

const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const valueStyle = {
    margin: 0,
    fontSize: 15,
    color: '#1a1a1a',
    lineHeight: 1.5,
    wordBreak: 'break-word',
};

const sectionCard = {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 20px 12px',
};

const loadingBox = {
    padding: '28px 20px',
    textAlign: 'center',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#64748b',
    fontSize: 14,
};

const ReadOnlyField = ({ label, children }) => {
    return (
        <FormGroup>
            <span style={labelStyle}>{label}</span>
            <div style={valueStyle}>{children}</div>
        </FormGroup>
    );
};

function formatPhoneList(phoneNumbers) {
    if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) return '—';

    const parts = phoneNumbers
        .map((p) => (typeof p === 'string' ? p : p?.number))
        .filter(Boolean)
        .map((raw) => {
            const digits = String(raw).replace(/\D/g, '').slice(0, 11);
            return digits.length ? maskPhone(digits) : null;
        })
        .filter(Boolean);

    return parts.length ? parts.join(' · ') : '—';
}

export default function UserProfileView() {
    const { user } = useAuth();
    const isTransportCompany = (user?.roles || []).includes('ROLE_TRANSPORT_COMPANY');
    const { selectedCustomerId } = useCustomerSelector();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [resolvedAddress, setResolvedAddress] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const dto = await employeeService.getProfile();
                if (!cancelled) setProfile(dto);
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar o perfil do usuário.');
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [reloadTick]);

    useEffect(() => {
        setResolvedAddress(null);
        const addressId = profile?.addressId;
        const customerIdForAddress = selectedCustomerId ?? profile?.customerId ?? null;
        if (!addressId || customerIdForAddress == null) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const dto = await addressService.getById(addressId, customerIdForAddress);
                if (!cancelled) setResolvedAddress(mapAddressFromApi(dto));
            } catch {
                if (!cancelled) setResolvedAddress(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [profile?.addressId, profile?.customerId, selectedCustomerId, reloadTick]);

    const headerSubtitle = useMemo(() => {
        const name = profile?.name || '';
        const email = profile?.email || '';
        if (!name && !email) return null;
        if (name && email) return `${name} • ${email}`;
        return name || email;
    }, [profile]);

    const removed = profile?.removed ?? profile?.active === false;
    const activeLabel = removed ? 'Inativo' : 'Ativo';

    const cpf = profile?.cpf ?? profile?.document ?? null;
    const birthDate = profile?.birthDate ?? null;

    const addressFlat =
        profile?.address && typeof profile.address === 'object' ? profile.address : null;
    const address = resolvedAddress ?? addressFlat ?? {};

    return (
        <Container>
            <Header>
                <div style={{ minWidth: 0, flex: '1 1 12rem' }}>
                    <h1>Perfil do usuário</h1>
                    {!loading && headerSubtitle && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#64748b',
                                lineHeight: 1.4,
                            }}
                        >
                            {headerSubtitle}
                        </p>
                    )}
                </div>
            </Header>

            {isTransportCompany && (
                <ProfileLogoWrap>
                    <ProfileLogoFrame>
                        <TransportCompanyLogo
                            format={TRANSPORT_COMPANY_LOGO_FORMATS.PNG}
                            fallback="Logo da transportadora"
                        />
                    </ProfileLogoFrame>
                </ProfileLogoWrap>
            )}

            {loading && (
                <div style={loadingBox} role="status" aria-live="polite">
                    Carregando dados…
                </div>
            )}

            {error && (
                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        padding: '14px 16px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 10,
                    }}
                    role="alert"
                >
                    <p style={{ color: '#b91c1c', margin: 0, fontSize: 14 }}>{error}</p>
                    <Button type="button" variant="outline" onClick={() => setReloadTick((v) => v + 1)}>
                        Tentar novamente
                    </Button>
                </div>
            )}

            {!loading && !error && profile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={sectionCard}>
                        <SectionTitle>Dados pessoais</SectionTitle>
                        <FormRow>
                            <ReadOnlyField label="ID">{profile?.id != null ? String(profile.id) : '—'}</ReadOnlyField>
                            <ReadOnlyField label="Nome">{profile?.name || '—'}</ReadOnlyField>
                            <ReadOnlyField label="E-mail">{profile?.email || '—'}</ReadOnlyField>
                        </FormRow>
                        <FormRow>
                            <ReadOnlyField label="CPF">{cpf ? maskCPF(String(cpf).replace(/\D/g, '')) : '—'}</ReadOnlyField>
                            <ReadOnlyField label="Data de nascimento">
                                {birthDate ? formatDateBR(String(birthDate)) || '—' : '—'}
                            </ReadOnlyField>
                            <ReadOnlyField label="Cargo">{profile?.jobTitle || '—'}</ReadOnlyField>
                        </FormRow>
                    </div>

                    <div style={sectionCard}>
                        <SectionTitle>Contato</SectionTitle>
                        <FormRow>
                            <ReadOnlyField label="Telefones">{formatPhoneList(profile?.phoneNumbers)}</ReadOnlyField>
                            <ReadOnlyField label="Ativo na plataforma">{activeLabel}</ReadOnlyField>
                        </FormRow>
                    </div>

                    <div style={sectionCard}>
                        <ReadOnlyAddressFields address={address} title="Endereço" />
                    </div>
                </div>
            )}
        </Container>
    );
}

